<template>
  <div>
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <input
          v-model="search"
          type="text"
          :placeholder="$t('bo.occupationSkills.searchRole')"
          class="search-input"
          @input="onSearch"
        />
        <select v-model="filterGroupId" @change="onFilterChange" class="filter-select">
          <option value="">{{ $t('bo.occupationSkills.allGroups') }}</option>
          <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
        </select>
      </div>
      <button @click="showCreate = true" class="btn-create">
        + {{ $t('bo.occupationSkills.createOccupation') }}
      </button>
    </div>

    <div v-if="loading" class="state-msg">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="roles.length === 0" class="state-msg">{{ $t('bo.occupationSkills.noRoles') }}</div>

    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.occupationSkills.colRole') }}</th>
          <th>{{ $t('bo.occupationSkills.colSubfamily') }}</th>
          <th>{{ $t('bo.occupationSkills.colGroup') }}</th>
          <th>{{ $t('bo.occupationSkills.colSkills') }}</th>
          <th>{{ $t('bo.occupationSkills.colDefault') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="role in roles" :key="role.id">
          <td class="col-name">
            <div class="name">{{ role.name }}</div>
            <div class="slug">{{ role.slug }}</div>
          </td>
          <td>{{ role.subfamily?.name || '—' }}</td>
          <td>
            <span class="group-tag">{{ role.subfamily?.group?.name || '—' }}</span>
          </td>
          <td>
            <div class="skills-cell">
              <span :class="['badge', role.skills_count > 0 ? 'badge-has' : 'badge-empty']">
                {{ role.skills_count }}
              </span>
              <span v-if="role.skills_by_kind?.tool > 0" class="kind-chip kind-chip--tool">{{ role.skills_by_kind.tool }} tool</span>
              <span v-if="role.skills_by_kind?.framework > 0" class="kind-chip kind-chip--fw">{{ role.skills_by_kind.framework }} fw</span>
              <span v-if="role.skills_by_kind?.certification > 0" class="kind-chip kind-chip--cert">{{ role.skills_by_kind.certification }} cert</span>
            </div>
          </td>
          <td>
            <span v-if="role.is_default_suggestion" class="badge badge-default">✓</span>
            <span v-else class="muted">—</span>
          </td>
          <td>
            <button @click="openRole(role.id)" class="btn-action">{{ $t('bo.common.edit') }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div v-if="meta && meta.last_page > 1" class="pagination">
      <button :disabled="meta.current_page === 1" @click="goToPage(meta.current_page - 1)" class="page-btn">‹</button>
      <span class="page-info">{{ $t('bo.occupationSkills.pageInfo', { current: meta.current_page, last: meta.last_page, total: meta.total }) }}</span>
      <button :disabled="meta.current_page === meta.last_page" @click="goToPage(meta.current_page + 1)" class="page-btn">›</button>
    </div>

    <EditOccupationRoleModal
      v-if="editingRole"
      :role="editingRole"
      @close="editingRole = null"
      @updated="handleUpdated"
    />

    <CreateOccupationModal
      v-if="showCreate"
      @close="showCreate = false"
      @created="handleCreated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import occupationSkillService from '@/services/occupationSkillService'

const { t } = useI18n()
import EditOccupationRoleModal from './EditOccupationRoleModal.vue'
import CreateOccupationModal from './CreateOccupationModal.vue'

const roles = ref([])
const groups = ref([])
const meta = ref(null)
const currentPage = ref(1)
const loading = ref(false)
const error = ref('')
const search = ref('')
const filterGroupId = ref('')
const editingRole = ref(null)
const showCreate = ref(false)
let searchTimer = null

const loadRoles = async () => {
  loading.value = true
  error.value = ''
  try {
    const params = { page: currentPage.value, per_page: 50 }
    if (search.value) params.search = search.value
    if (filterGroupId.value) params.group_id = filterGroupId.value
    const res = await occupationSkillService.getRoles(params)
    roles.value = res.data || []
    meta.value = res.meta || null
  } catch (e) {
    error.value = t('bo.occupationSkills.failedLoadRoles')
  } finally {
    loading.value = false
  }
}

const loadGroups = async () => {
  try {
    const res = await occupationSkillService.getGroups()
    groups.value = res.data || []
  } catch (e) {}
}

const onSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => { currentPage.value = 1; loadRoles() }, 350)
}

const onFilterChange = () => { currentPage.value = 1; loadRoles() }

const goToPage = (page) => { currentPage.value = page; loadRoles() }

const openRole = async (roleId) => {
  try {
    const res = await occupationSkillService.getRole(roleId)
    editingRole.value = res.data
  } catch (e) {
    alert(t('bo.occupationSkills.failedLoadRole'))
  }
}

const handleUpdated = () => {
  editingRole.value = null
  loadRoles()
}

const handleCreated = () => {
  showCreate.value = false
  loadRoles()
}

onMounted(() => { loadGroups(); loadRoles() })
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.toolbar-left { display: flex; gap: 0.75rem; }
.btn-create {
  padding: 0.5rem 1.25rem; background: #3b82f6; color: white;
  border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
}
.btn-create:hover { background: #2563eb; }
.search-input { padding: 0.5rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem; width: 260px; }
.filter-select { padding: 0.5rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem; background: white; }

.state-msg { padding: 3rem; text-align: center; color: #6b7280; background: white; border-radius: 0.75rem; border: 1px solid #e5e7eb; }
.error-msg { padding: 1rem; color: #dc2626; background: #fef2f2; border-radius: 0.5rem; border: 1px solid #fecaca; }

.table { width: 100%; border-collapse: collapse; font-size: 0.875rem; background: white; border-radius: 0.75rem; border: 1px solid #e5e7eb; overflow: hidden; }
.table th { padding: 0.75rem 1rem; text-align: left; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
.table td { padding: 0.75rem 1rem; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
.table tr:last-child td { border-bottom: none; }

.col-name { max-width: 220px; }
.name { font-weight: 600; color: #1f2937; }
.slug { font-size: 0.75rem; color: #9ca3af; }
.group-tag { font-size: 0.8rem; background: #eff6ff; color: #1d4ed8; padding: 0.15rem 0.5rem; border-radius: 9999px; }
.muted { color: #9ca3af; }

.badge { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; }
.badge-has    { background: #d1fae5; color: #065f46; }
.badge-empty  { background: #f3f4f6; color: #6b7280; }
.badge-default{ background: #fef3c7; color: #92400e; }

.skills-cell { display: flex; align-items: center; gap: 0.3rem; flex-wrap: wrap; }
.kind-chip { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 0.1rem 0.35rem; border-radius: 9999px; }
.kind-chip--tool  { background: #fef3c7; color: #92400e; }
.kind-chip--fw    { background: #dbeafe; color: #1d4ed8; }
.kind-chip--cert  { background: #ede9fe; color: #6d28d9; }

.btn-action { padding: 0.3rem 0.65rem; border-radius: 0.375rem; font-size: 0.8rem; cursor: pointer; border: 1px solid #3b82f6; color: #3b82f6; background: white; font-weight: 500; }
.btn-action:hover { background: #eff6ff; }

.pagination { display: flex; align-items: center; justify-content: center; gap: 1rem; margin-top: 1rem; }
.page-btn { padding: 0.35rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.375rem; background: white; cursor: pointer; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-btn:hover:not(:disabled) { background: #f3f4f6; }
.page-info { font-size: 0.875rem; color: #6b7280; }
</style>
